/**
 * Part 23c, table WC: the comments of a Word file's comments part (ECMA-376 §17.13.4), each as plain text (decision 59
 * applied to Word's comments): its `w:id`, author, date and the text of its paragraphs. Pure.
 */
import { attribute, childElements, readXml } from '../../xml.ts';
import type { XmlElement } from '../../xml.ts';
import { NS } from '../package.ts';

/** WC7: the most comments a file's part gives; the rest are left out, flagged. */
export const MAX_COMMENTS = 2_000;

/** WC6: the longest comment text, in UTF-16 units; a longer comment is left out, flagged. */
export const MAX_COMMENT_TEXT = 20_000;

/** WC1: the longest author a comment keeps (the rest cut), in UTF-16 units. */
export const MAX_AUTHOR = 200;

/** WC9: the longest date as written a comment keeps; a longer one is none. */
export const MAX_DATE = 64;

/** One comment of the part (WC1): its `w:id`, its author (empty with none), its date as written (null with none) and its text. */
export interface WordComment {
  readonly id: string;
  readonly author: string;
  readonly date: string | null;
  readonly text: string;
}

/** The part's comments in its order, and how many were left out (WC4–WC7). */
export interface WordComments {
  readonly comments: readonly WordComment[];
  readonly left: number;
}

/** WC1, WC2: the text `element` holds as a comment's: `w:t` text, a tab, a line end at a break, none of a deletion's. */
function commentText(element: XmlElement, out: string[]): void {
  for (const node of element.children) {
    if (typeof node === 'string') {
      if (element.ns === NS.w && element.name === 't') out.push(node);
      continue;
    }
    if (node.ns !== NS.w) continue;
    if (node.name === 'tab') out.push('\t');
    else if (node.name === 'br' || node.name === 'cr') out.push('\n');
    else if (node.name !== 'del' && node.name !== 'moveFrom') commentText(node, out);
  }
}

/** WC1: a comment's paragraphs' texts joined by line ends. */
function textOfComment(comment: XmlElement): string {
  const paragraphs: string[] = [];
  const walk = (element: XmlElement): void => {
    for (const node of childElements(element)) {
      if (node.ns !== NS.w || node.name === 'del' || node.name === 'moveFrom') continue;
      if (node.name !== 'p') {
        walk(node);
        continue;
      }
      const out: string[] = [];
      commentText(node, out);
      paragraphs.push(out.join(''));
    }
  };
  walk(comment);
  return paragraphs.join('\n');
}

/**
 * WC1–WC7: the comments of `bytes`, a comments part (after `xmlBytes`). `tick` is called as the XML is read (part 18's
 * table CO). Throws what the XML reader throws for a part it refuses (part 18's table X), a comment past its node cap
 * included.
 */
export function readComments(bytes: Uint8Array, tick: () => void): WordComments {
  const comments: WordComment[] = [];
  const ids = new Set<string>();
  let left = 0;
  readXml(bytes, {
    progress: tick,
    open: (element, depth) => {
      if (element.ns !== NS.w) return 'skip';
      if (depth === 0) return element.name === 'comments' ? 'descend' : 'skip';
      return element.name === 'comment' ? 'take' : 'skip';
    },
    take: (element) => {
      const id = attribute(element, NS.w, 'id');
      const text = textOfComment(element);
      // WC4–WC7: no ID, an ID met before, a text past the cap, or past the count: left out.
      if (id === undefined || ids.has(id) || text.length > MAX_COMMENT_TEXT || comments.length >= MAX_COMMENTS) {
        left += 1;
        return;
      }
      ids.add(id);
      const date = attribute(element, NS.w, 'date') ?? null;
      comments.push({ id, author: (attribute(element, NS.w, 'author') ?? '').slice(0, MAX_AUTHOR), date: date !== null && date.length <= MAX_DATE ? date : null, text });
    },
  });
  return { comments, left };
}

/** WC9: an ISO 8601 date and time as Word writes `w:date`: the date, the time to minutes or seconds or a fraction, and a zone or none. */
const DATE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(?:Z|([+-])(\d{2}):(\d{2}))?$/u;

/** Days in each month of a common year. */
const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * WC9: the time `date` names, in milliseconds since the Unix epoch: a date and time {@link DATE} matches, every field
 * within its range (an offset up to 14 hours), UTC when it names no zone; null for anything else, or a time before 1970.
 */
export function commentTime(date: string): number | null {
  const match = DATE.exec(date);
  if (match === null) return null;
  // A group the date leaves out is NaN: no comparison holds for it, and `|| 0` makes it none.
  const field = (group: number): number => Number(match[group]);
  const [year, month, day, hour, minute, second] = [field(1), field(2), field(3), field(4), field(5), field(6) || 0];
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = month === 2 && leap ? 29 : (MONTH_DAYS[month - 1] ?? 0);
  const offset = (match[8] === '-' ? -1 : 1) * ((field(9) || 0) * 60 + (field(10) || 0));
  if (day < 1 || day > days || hour > 23 || minute > 59 || second > 59 || field(10) > 59 || Math.abs(offset) > 14 * 60) return null;
  const time = Date.UTC(year, month - 1, day, hour, minute, second, Math.floor(Number(`0.${match[7] ?? '0'}`) * 1000)) - offset * 60_000;
  return time < 0 ? null : time;
}
